import apiClient from '../utils/apiClient';

const BASE_PATHS = {
  project: '/api/projects',
  lead: '/api/leads',
};

function basePath(entityType) {
  const base = BASE_PATHS[entityType];
  if (!base) throw new Error(`Unknown entity type: ${entityType}`);
  return base;
}

export async function getActivity(entityType, entityId, { limit, before } = {}) {
  const response = await apiClient.get(`${basePath(entityType)}/${entityId}/activity`, {
    params: { limit, before },
  });
  return response.data.activity;
}

export async function addComment(entityType, entityId, commentText) {
  const response = await apiClient.post(`${basePath(entityType)}/${entityId}/comments`, {
    comment_text: commentText,
  });
  return response.data.activity;
}

export async function listFiles(entityType, entityId) {
  const response = await apiClient.get(`${basePath(entityType)}/${entityId}/files`);
  return response.data.files;
}

export async function uploadFile(entityType, entityId, file) {
  const formData = new FormData();
  formData.append('file', file);
  const response = await apiClient.post(`${basePath(entityType)}/${entityId}/files`, formData);
  return response.data.file;
}

export async function deleteFile(entityType, entityId, fileId) {
  const response = await apiClient.delete(`${basePath(entityType)}/${entityId}/files/${fileId}`);
  return response.data;
}

// Attachments live in a private bucket, so there is no durable URL to link to.
// The server mints a short-lived signed URL per download, which keeps the
// permission check at click time and stops a link going stale in an open tab.
//
// The tab is opened synchronously from the click and its location set after
// the request resolves — opening it afterwards would be blocked as a popup.
export async function openAttachment(entityType, entityId, fileId) {
  // The tab is opened synchronously from the click and pointed at the URL once
  // it arrives — opening it after the await would be treated as a popup.
  //
  // Note the absence of "noopener": window.open returns null when it is set,
  // which loses the handle to the tab just created. The opener reference is
  // dropped below instead, which gives the same protection without that.
  const tab = window.open('', '_blank');
  if (tab) {
    try {
      tab.opener = null;
    } catch (err) {
      /* some browsers make this read-only; the tab is cross-origin regardless */
    }
  }

  try {
    const { data } = await apiClient.get(
      `${basePath(entityType)}/${entityId}/files/${fileId}/download`
    );
    if (tab && !tab.closed) {
      tab.location.href = data.url;
    } else {
      // Only reached if the popup was blocked or the tab was closed while the
      // URL was being fetched. Navigating the current tab is the fallback.
      window.location.href = data.url;
    }
  } catch (err) {
    if (tab && !tab.closed) tab.close();
    throw err;
  }
}
